// src/components/settings/catalog/CatalogSuppliersTab.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2.
//
// Suppliers tab: the supplier list (catalogSuppliers — office roles read it; never written from
// here), "+ New supplier", edit in place, archive / restore. Saves through saveCatalogSupplier.
import React, { useState } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import { LIMITS } from '../../../utils/catalogUi';
import { saveCatalogSupplier, countCatalogSupplierItems, friendlyError } from './catalogApi';
import { BTN_GHOST, BTN_PRIMARY, BTN_DANGER, INPUT, LABEL, Req, Opt, ErrorLine, Notice } from './catalogUiParts';

const FIELDS = [
    ['contactName', 'Contact name', LIMITS.supplierText],
    ['phone', 'Phone', LIMITS.supplierText],
    ['email', 'Email', LIMITS.supplierText],
    ['accountNumber', 'Account #', LIMITS.supplierText],
];
const blank = () => ({ id: null, name: '', contactName: '', phone: '', email: '', accountNumber: '', notes: '' });

export default function CatalogSuppliersTab({ suppliers, loadError, newSignal }) {
    const [editor, setEditor] = useState(null); // null | { draft, saving, error }
    const [notice, setNotice] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const lastSignal = React.useRef(newSignal);

    React.useEffect(() => {
        if (newSignal !== lastSignal.current) {
            lastSignal.current = newSignal;
            setNotice(null);
            setEditor({ draft: blank(), saving: false, error: '' });
        }
    }, [newSignal]);

    const open = (s) => { setNotice(null); setEditor({ draft: { ...blank(), ...s, notes: s.notes || '' }, saving: false, error: '' }); };
    const set = (patch) => setEditor((e) => ({ ...e, error: '', draft: { ...e.draft, ...patch } }));

    const save = async (extra = {}) => {
        const d = editor.draft;
        if (!d.name.trim()) { setEditor((e) => ({ ...e, error: 'Please give the supplier a name.' })); return; }
        setEditor((e) => ({ ...e, saving: true, error: '' }));
        const input = { name: d.name.trim(), notes: d.notes.trim(), ...extra };
        for (const [k] of FIELDS) input[k] = d[k] && String(d[k]).trim() ? String(d[k]).trim() : null;
        if (d.id) input.supplierId = d.id;
        try {
            await saveCatalogSupplier(input);
            setEditor(null);
            setNotice({ text: extra.archived === true ? `Archived “${input.name}”.` : extra.archived === false ? `Restored “${input.name}”.` : `Saved “${input.name}”.` });
        } catch (err) {
            setEditor((e) => ({ ...e, saving: false, error: friendlyError(err, "Couldn't save the supplier. Try again.") }));
        }
    };

    const list = suppliers ? suppliers.filter((s) => (showArchived ? s.archived === true : s.archived !== true)) : null;
    const archivedCount = suppliers ? suppliers.filter((s) => s.archived === true).length : 0;

    // catscreen-7a2c4e91 Stage 4 — how many (non-archived) items use each supplier shown, asked of the
    // server for just these suppliers, 100 at a time. A count still on its way shows "—".
    const [itemCounts, setItemCounts] = useState({});
    const idsKey = list ? list.map((s) => s.id).join(',') : '';
    React.useEffect(() => {
        if (!idsKey) return undefined;
        let alive = true;
        const ids = idsKey.split(',');
        (async () => {
            for (let i = 0; i < ids.length && alive; i += 100) {
                try {
                    const r = await countCatalogSupplierItems(ids.slice(i, i + 100));
                    if (alive) setItemCounts((c) => ({ ...c, ...(r.counts || {}) }));
                } catch (err) {
                    console.error('Catalog: supplier counts failed', err); // a count is a nicety: the column just shows "—"
                }
            }
        })();
        return () => { alive = false; };
    }, [idsKey, notice]);

    return (
        <div className="space-y-4">
            <p className="text-sm text-gray-600 p-3 rounded-lg bg-gray-50 border border-gray-200">Office only. Customers and the portal never see your suppliers.</p>
            <Notice notice={notice} onClose={() => setNotice(null)} />

            {editor && (
                <div className="p-4 rounded-xl border-2 border-[#5F4EC9]/40 bg-[#5F4EC9]/[0.03] space-y-3">
                    <h3 className="font-semibold text-gray-800">{editor.draft.id ? `Edit ${editor.draft.name || 'supplier'}` : 'New supplier'}</h3>
                    <div>
                        <label htmlFor="cs-name" className={LABEL}>Name<Req /></label>
                        <input id="cs-name" className={INPUT} autoFocus maxLength={LIMITS.supplierName} value={editor.draft.name} onChange={(e) => set({ name: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {FIELDS.map(([k, label, max]) => (
                            <div key={k}>
                                <label htmlFor={`cs-${k}`} className={LABEL}>{label}<Opt /></label>
                                <input id={`cs-${k}`} className={INPUT} maxLength={max} value={editor.draft[k] || ''} onChange={(e) => set({ [k]: e.target.value })}
                                    type={k === 'email' ? 'email' : k === 'phone' ? 'tel' : 'text'} />
                            </div>
                        ))}
                    </div>
                    <div>
                        <label htmlFor="cs-notes" className={LABEL}>Notes<Opt /></label>
                        <textarea id="cs-notes" className={`${INPUT} py-2`} rows={2} maxLength={LIMITS.supplierNotes} value={editor.draft.notes} onChange={(e) => set({ notes: e.target.value })} />
                    </div>
                    <ErrorLine text={editor.error} />
                    <div className="flex flex-wrap items-center gap-2">
                        {editor.draft.id && (
                            <button type="button" className={editor.draft.archived ? BTN_GHOST : BTN_DANGER} disabled={editor.saving}
                                onClick={() => save({ archived: !editor.draft.archived })}>
                                {editor.draft.archived ? 'Restore' : 'Archive'}
                            </button>
                        )}
                        <div className="flex-1" />
                        <button type="button" className={BTN_GHOST} disabled={editor.saving} onClick={() => setEditor(null)}>Cancel</button>
                        <button type="button" className={BTN_PRIMARY} disabled={editor.saving} onClick={() => save()}>{editor.saving ? 'Saving…' : 'Save'}</button>
                    </div>
                    {editor.draft.id && !editor.draft.archived && <p className="text-xs text-gray-500">Archiving a supplier keeps it on the items that already use it. It just can't be added to more.</p>}
                </div>
            )}

            {loadError && <ErrorLine text={loadError} />}
            {!loadError && list === null && <p className="text-sm text-gray-500"><Loader2 size={14} className="inline animate-spin mr-2" />Loading your suppliers…</p>}
            {list && list.length === 0 && (
                <p className="py-8 text-center text-sm text-gray-500 rounded-xl border border-dashed border-gray-300 bg-gray-50">
                    {showArchived ? 'No archived suppliers.' : 'No suppliers yet. Use “+ New supplier” to add one.'}
                </p>
            )}
            {list && list.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[620px] text-sm">
                        <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <tr>
                                <th scope="col" className="px-4 py-3">Supplier</th>
                                <th scope="col" className="px-3 py-3">Account #</th>
                                <th scope="col" className="px-3 py-3">Phone</th>
                                <th scope="col" className="px-3 py-3 text-right">Items</th>
                                <th scope="col" className="px-3 py-3"><span className="sr-only">Edit</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {list.map((s) => (
                                <tr key={s.id}>
                                    <td className="px-4 py-2">
                                        <div className="font-medium text-gray-900">{s.name}</div>
                                        {s.contactName && <div className="text-xs text-gray-400">{s.contactName}</div>}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">{s.accountNumber || '—'}</td>
                                    <td className="px-3 py-2 text-gray-600">{s.phone || '—'}</td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">{typeof itemCounts[s.id] === 'number' ? itemCounts[s.id].toLocaleString('en-US') : '—'}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button type="button" className={BTN_GHOST} onClick={() => open(s)} aria-label={`Edit ${s.name}`}><Pencil size={14} /> Edit</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {(archivedCount > 0 || showArchived) && (
                <button type="button" className={BTN_GHOST} onClick={() => setShowArchived((v) => !v)}>
                    {showArchived ? 'Back to suppliers in use' : `Show archived suppliers (${archivedCount})`}
                </button>
            )}
        </div>
    );
}
